import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShieldAlert, LineChart, Gift, Sparkles, LogOut, ArrowLeft } from "lucide-react";
import { usePortalAuth } from "@/context/PortalAuthContext";

const NAV = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/portal/report", label: "Report", icon: ShieldAlert, testid: "nav-report" },
  { to: "/portal/progress", label: "Progress", icon: LineChart, testid: "nav-progress" },
  { to: "/portal/refer", label: "Refer", icon: Gift, testid: "nav-refer" },
  { to: "/portal/pricing", label: "Plans", icon: Sparkles, testid: "nav-pricing" },
];

export const PortalLayout = ({ children, back, title }) => {
  const { user, logout } = usePortalAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#020617]" data-testid="portal-layout">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.35]"
        style={{
          background:
            "radial-gradient(60rem 40rem at 85% -10%, rgba(212,175,55,0.10), transparent 60%), radial-gradient(50rem 30rem at 5% 10%, rgba(56,89,163,0.14), transparent 65%)",
        }}
      />
      <header className="sticky top-0 z-40 glass border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="font-serif text-lg md:text-xl text-white tracking-tight shrink-0" data-testid="portal-home-link">
            Bad Boss <span className="text-[#D4AF37]">Clinic</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map(({ to, label, icon: Icon, testid }) => {
              const active = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={`flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.14em] transition-colors ${
                    active ? "text-[#D4AF37] bg-[#D4AF37]/10" : "text-slate-400 hover:text-white"
                  }`}
                >
                  <Icon size={14} />
                  {label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            {user && (
              <span className="hidden sm:inline text-xs text-slate-500 uppercase tracking-widest" data-testid="portal-user-tier">
                {user.tier === "free" ? "Free plan" : "Premium"}
              </span>
            )}
            <button
              onClick={() => {
                logout();
                navigate("/portal/login");
              }}
              data-testid="portal-logout-btn"
              className="flex items-center gap-2 text-slate-400 hover:text-white text-xs uppercase tracking-widest transition-colors"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        <nav className="md:hidden flex border-t border-slate-800">
          {NAV.map(({ to, label, icon: Icon }) => {
            const active = location.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                className={`flex-1 flex flex-col items-center gap-1 py-2 text-[10px] uppercase tracking-widest ${
                  active ? "text-[#D4AF37]" : "text-slate-500"
                }`}
              >
                <Icon size={14} />
                {label.split(" ")[0]}
              </Link>
            );
          })}
        </nav>
      </header>

      <main className="relative max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12">
        {(back || title) && (
          <div className="mb-8 flex items-center gap-4">
            {back && (
              <button
                onClick={() => navigate(back)}
                data-testid="portal-back-btn"
                className="flex items-center gap-2 text-slate-500 hover:text-[#D4AF37] text-xs uppercase tracking-widest transition-colors"
              >
                <ArrowLeft size={14} /> Back
              </button>
            )}
            {title && <h1 className="font-serif text-2xl md:text-3xl text-white">{title}</h1>}
          </div>
        )}
        {children}
      </main>

      <footer className="relative max-w-6xl mx-auto px-5 md:px-8 pb-10">
        <p className="text-slate-600 text-xs border-t border-slate-900 pt-6" data-testid="portal-entity-line">
          Bad Boss Clinic is a service of Apex Advisory. Payments and invoices are issued by Apex Advisory.
        </p>
      </footer>
    </div>
  );
};

export default PortalLayout;
